// IMPORTANT NOTE: The structured table diffing algorithm implemented below is specifically designed for
// the structured table submit action audit event. Consequently, it makes particular assumptions about what
// constitutes a table diff that may not be appropriate for other use cases.
// 1. Non-finalized rows (previously unsubmitted rows) values are treated as null.
// 2. Only the evaluated, typed values are compared; if a change is made that cannot be mapped to the proper
//    type, it will not be detected.
//
// Additionally, The code uses some anti-patterns that makes it difficult to understand and maintain, however
// at time of writing we want to ensure it continues to behave as-is until we have better test coverage for
// the audit events it is used to generate. Once that test coverage is in place, we can refactor this code to
// follow best practices (such as not having side-effects in functions).
//
// TODO(BNCH-107376): Refactor the implementation according to best practices after test coverage is improved.

import produce from 'immer';
import {Set} from 'immutable';

import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTable,
  StructuredTableRow,
  UnknownStructuredTable,
  UnknownStructuredTableRow,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {updateStructuredTableUsingRowUpdater} from 'notebook/tables/refresh-table-data/updateStructuredTableUsingRowUpdater';
import {evaluateTables} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {Cell, TypedValue} from 'notebook/tables/TableConstants';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';

export interface TableSubmissionDiffs {
  // Update data used for logging table submmission audit events. This is the diff between the current table values
  // and the state of the table if it had been resynced i.e. the current entity values.
  tableSubmissionUpdates: Array<StructuredTableDiff>;
  // Expected differences between audits logged based on the above data and audits logged from LSUV diffing.
  // Used to validate audits generated from the patcher. Any difference between audits generated from the above data
  // and audits generated from the patcher, excluding these expected differences, will be flagged.
  expectedTableSubmissionAuditDiffs: Array<StructuredTableDiff>;
}

export interface StructuredTableDiff {
  createdDate: Date;
  rowDiffs: Array<StructuredTableRowDiff>;
}

// TODO(BNCH-36124): Once processes are implemented, this exclusion can be removed
const UnsupportedStructuredTableKinds = ['PROCESS_OUTPUT', 'PROCESS_TASK', 'RUN_OUTPUT_SAMPLES'];

export function isStructuredTableUnsupported(structuredTables: Array<UnknownStructuredTable>): boolean {
  return structuredTables
    .map((structuredTable) => structuredTable.kind)
    .some((kind) => UnsupportedStructuredTableKinds.includes(kind));
}

interface StructuredTableRowDiff {
  rowId: string;
  isFinalized: boolean;
  cellDiffs: Array<StructuredTableCellDiff>;
}

export interface StructuredTableCellDiff {
  columnProvenanceUuid: string;
  oldValue: TypedValue | null;
  newValue: TypedValue | null;
}

interface UuidTableMap {
  rowIds: UuidRowMap;
  columnIds: UuidRowMap;
}

interface UuidRowMap {
  [id: string]: number;
}

/**
 * Create a diff of a modified structured table(s) and its previous saved values when a user clicks submit. The previous values
 * are retrieved using updateStructuredTableUsingRowUpdater which mimics a refresh of the table and provides the most up-to-date table.
 * Must be able to handle an array of values because of Note Linked Groups which can have multiple structured tables
 *
 * @param computedTables
 * @param structuredTables
 * @param structuredTableAdapters
 */
export function getStructuredTableCellDiffOnSubmission(
  computedTables: Array<EvaluatedTable>,
  structuredTables: Array<UnknownStructuredTable>,
  structuredTableAdapters: Array<UnknownStructuredTableAdapter>
): Promise<TableSubmissionDiffs> {
  const structuredTableDiffResults = computedTables.map(
    async (
      computedTable,
      idx
    ): Promise<{
      tableSubmissionUpdates: StructuredTableDiff;
      expectedTableSubmissionAuditDiffs: StructuredTableDiff;
    }> =>
      await getSingleStructuredTableCellDiffOnSubmission(
        computedTable,
        structuredTables[idx],
        structuredTableAdapters[idx]
      )
  );

  return Promise.all(structuredTableDiffResults).then((results) => ({
    tableSubmissionUpdates: results.map((result) => result.tableSubmissionUpdates),
    expectedTableSubmissionAuditDiffs: results.map((result) => result.expectedTableSubmissionAuditDiffs),
  }));
}

async function getSingleStructuredTableCellDiffOnSubmission(
  computedTable: EvaluatedTable,
  structuredTable: UnknownStructuredTable,
  structuredTableAdapter: UnknownStructuredTableAdapter
): Promise<{
  tableSubmissionUpdates: StructuredTableDiff;
  expectedTableSubmissionAuditDiffs: StructuredTableDiff;
}> {
  // Using the RowUpdater's update functionality will produce a table with the most up-to-date values regarding
  // the primary samples present in the table, *without* the user's current edits. This will allow us to identify
  // which values not currently in the table would be overwritten by table submission. Thus, this virtual table
  // represents the "current" state of the table, and the parameter `structuredTable` represents the  *new*
  // state of table.
  let virtualResyncedTable = await updateStructuredTableUsingRowUpdater({
    rowUpdaters: structuredTableAdapter.getAllRowUpdaters(structuredTable.columns),
    samplesByRowId: structuredTableAdapter.getPrimarySamples(structuredTable, computedTable),
    structuredTable,
  });

  // Since we are using the evaluator to produce the computed table, we need to make sure to clear out
  // any formulas if a typedValue exists.   We should have all the typedValues populated by the
  // lastSyncedUpstreamValues, and we want to keep those values and not let evalation run formulas
  virtualResyncedTable = produce(virtualResyncedTable, (draftVirtualTable) => {
    draftVirtualTable.rows.forEach((row) => {
      row.cells.forEach((cell) => {
        if (cell.typedValue) {
          cell.text = '';
        }
      });
    });
  });

  const adapter = getStructuredTableAdapters()[virtualResyncedTable.kind];
  const {computedTables} = evaluateTables({
    fullTableMap: {[virtualResyncedTable.cid]: adapter.toTable(virtualResyncedTable)},
    oldComputedTables: null,
    hasFormulasDisabled: false,
    selectors: [],
    spreadsheetEvalCacheKey: null,
    fileId: structuredTable.fileId,
  });
  const tableSubmissionUpdates = generateStructuredTableDiff(
    {structuredTable: virtualResyncedTable, computedTable: computedTables[virtualResyncedTable.cid]},
    {structuredTable, computedTable}
  );

  // For validation purposes (see benchling/notebook/structured_tables/audit/utils/validate_submission_audits.py)
  // we also want to determine the expected differences between audits generated from tableSubmissionUpdates above
  // and audits generated from the patcher, which diff against the previous LSUVS instead of the resynced values.
  // To compute these create a second virtual table where typedValues are replaced with lastSyncedUpstreamValues
  // and diff that table against the virtual resynced table.
  const {virtualUpstreamStructuredTable, virtualUpstreamComputedTable} = createVirtualUpstreamTable(
    structuredTable,
    computedTable
  );

  const expectedTableSubmissionAuditDiffs = generateStructuredTableDiff(
    {structuredTable: virtualResyncedTable, computedTable: computedTables[virtualResyncedTable.cid]},
    {structuredTable: virtualUpstreamStructuredTable, computedTable: virtualUpstreamComputedTable}
  );
  // Newly submitted row audits are expected to be the same
  expectedTableSubmissionAuditDiffs.rowDiffs = expectedTableSubmissionAuditDiffs.rowDiffs.filter(
    (rowDiff) => rowDiff.isFinalized
  );

  return {
    tableSubmissionUpdates,
    expectedTableSubmissionAuditDiffs,
  };
}

/*
  Creates a table with the lastSyncedUpstreamValues as the table values
*/
function createVirtualUpstreamTable(
  structuredTable: UnknownStructuredTable,
  computedTable: EvaluatedTable
): {virtualUpstreamStructuredTable: UnknownStructuredTable; virtualUpstreamComputedTable: EvaluatedTable} {
  const virtualUpstreamStructuredTable = produce(structuredTable, (draftTable) => {
    for (const row of draftTable.rows) {
      if (!row.lastSyncedUpstreamValues?.length) {
        continue;
      }
      const upstreamValuesByColumn = new Map();
      for (const upstreamValue of row.lastSyncedUpstreamValues) {
        if (upstreamValue.columnProvenanceUuid) {
          upstreamValuesByColumn.set(upstreamValue.columnProvenanceUuid, upstreamValue.value);
        }
      }

      for (const [columnIndex, cell] of row.cells.entries()) {
        const columnUuid = structuredTable.columns[columnIndex]?.provenanceUuid;
        if (columnUuid && upstreamValuesByColumn.has(columnUuid)) {
          cell.typedValue = upstreamValuesByColumn.get(columnUuid) || null;
          cell.text = '';
        }
      }
    }
  });

  // Unlike the row values in the virtual resynced table which need to be evaluated, the upstream values are already evalated
  const virtualUpstreamComputedTable = computedTable.map((row, rowIndex) =>
    row.map((cell, cellIndex) => ({
      ...cell,
      value: virtualUpstreamStructuredTable.rows[rowIndex]?.cells[cellIndex]?.typedValue || cell.value,
    }))
  );

  return {
    virtualUpstreamStructuredTable,
    virtualUpstreamComputedTable,
  };
}

/**
 * A generic function which can generate a diff given two structured tables
 * and their computed tables
 *
 * @param oldTable
 * @param newTable
 */
function generateStructuredTableDiff<TTableMetadata, TRowMetadata>(
  oldTable: {
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
    computedTable: Array<Array<EvaluatedCell>>;
  },
  newTable: {
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
    computedTable: Array<Array<EvaluatedCell>>;
  }
): StructuredTableDiff {
  const uuidMapOldStructuredTable = uuidBuildMap(oldTable.structuredTable);
  const uuidMapNewStructuredTable = uuidBuildMap(newTable.structuredTable);
  const uniqueRowIds: Set<string> = Set(
    Object.keys(uuidMapNewStructuredTable.rowIds).concat(Object.keys(uuidMapOldStructuredTable.rowIds))
  );
  const uniqueColumnIds: Set<string> = Set(
    Object.keys(uuidMapNewStructuredTable.columnIds).concat(Object.keys(uuidMapOldStructuredTable.columnIds))
  );

  const rowDiffs: Array<StructuredTableRowDiff> = [];
  uniqueRowIds.forEach((rowId) => {
    const newRowIdx = uuidMapNewStructuredTable.rowIds[rowId];
    const oldRowIdx = uuidMapOldStructuredTable.rowIds[rowId];
    const newRow = newTable.structuredTable.rows[newRowIdx];
    const oldRow = oldTable.structuredTable.rows[oldRowIdx];

    const newRowParsed = _extractEvaluatedValues(newRow, newTable.computedTable[newRowIdx]);
    const oldRowParsed = _extractEvaluatedValues(oldRow, oldTable.computedTable[oldRowIdx]);

    generateStructuredTableRowDiff(
      newRowParsed,
      uuidMapNewStructuredTable,
      oldRowParsed,
      uuidMapOldStructuredTable,
      rowDiffs,
      uniqueColumnIds,
      rowId
    );
  });

  return {createdDate: new Date(), rowDiffs};
}

function generateStructuredTableRowDiff(
  newRow: UnknownStructuredTableRow | undefined,
  idMapNewStructuredTable: UuidTableMap,
  oldRow: UnknownStructuredTableRow | undefined,
  idMapOldStructuredTable: UuidTableMap,
  rowDiffs: Array<StructuredTableRowDiff>,
  uniqueColumnId: Set<string>,
  rowId: string
): void {
  const diffedCells: Array<StructuredTableCellDiff> = [];

  uniqueColumnId.forEach((columnId) => {
    const newCell = newRow?.cells[idMapNewStructuredTable.columnIds[columnId]];
    const oldCell = oldRow?.cells[idMapOldStructuredTable.columnIds[columnId]];
    generateStructuredTableCellDiff(newCell, oldCell, diffedCells, columnId, !newRow?.isFinalized);
  });

  if (diffedCells.length > 0) {
    rowDiffs.push({
      isFinalized: newRow !== undefined ? newRow.isFinalized : false,
      cellDiffs: diffedCells,
      rowId,
    });
  }
}

function generateStructuredTableCellDiff(
  newCell: Cell | undefined,
  oldCell: Cell | undefined,
  diffedCells: Array<StructuredTableCellDiff>,
  columnId: string,
  isNewRow: boolean
): void {
  if (
    newCell !== undefined &&
    oldCell !== undefined &&
    (isNewRow || !areTypedValuesEquivalent(newCell.typedValue!, oldCell.typedValue!))
  ) {
    diffedCells.push({
      columnProvenanceUuid: columnId,
      oldValue: !isNewRow ? oldCell.typedValue! : null,
      newValue: newCell.typedValue!,
    });
  } else if (newCell === undefined || oldCell === undefined) {
    diffedCells.push({
      columnProvenanceUuid: columnId,
      oldValue: oldCell !== undefined ? oldCell.typedValue! : null,
      newValue: newCell !== undefined ? newCell.typedValue! : null,
    });
  }
}

export function structuredTablesContainFinalizedRow(
  structuredTables: Array<UnknownStructuredTable>
): boolean {
  return structuredTables
    .map((structuredTable) => structuredTable.rows.map((row) => row.isFinalized).includes(true))
    .includes(true);
}

export function getStructuredTableDiffCount(tableDiffs: Array<StructuredTableDiff>): number {
  return tableDiffs
    .map((tableDiff) => _getSingleDiffCount(tableDiff))
    .reduce((accumulator, diffCount) => accumulator + diffCount, 0);
}

function _getSingleDiffCount(tableDiff: StructuredTableDiff): number {
  return tableDiff.rowDiffs
    .map((row) => row.cellDiffs.length)
    .reduce((accumulator, diffCount) => accumulator + diffCount, 0);
}

function _extractEvaluatedValues<TRowMetadata>(
  structuredTableRow: StructuredTableRow<TRowMetadata>,
  computedTableRow: Array<EvaluatedCell>
): StructuredTableRow<TRowMetadata> | undefined {
  if (structuredTableRow === undefined || computedTableRow === undefined) {
    return undefined;
  }
  return {
    ...structuredTableRow,
    cells: structuredTableRow.cells.map((cell, cidx) => ({
      ...cell,
      typedValue: computedTableRow[cidx].value,
    })),
  };
}

function uuidBuildMap(structuredTable: UnknownStructuredTable): UuidTableMap {
  const rowMap: UuidRowMap = {} as UuidRowMap;
  const colMap: UuidRowMap = {} as UuidRowMap;
  structuredTable.rows.forEach((row, idx) => {
    rowMap[row.rowId] = idx;
  });
  structuredTable.columns.forEach((col, idx) => {
    colMap[col.provenanceUuid] = idx;
  });
  return {
    rowIds: rowMap,
    columnIds: colMap,
  };
}
